import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAuth } from "../App";
import { Calendar as CalendarIcon, Plus, Link2, Clock, User, Copy, Send, Check, X, Trash2, Download, FileText, RefreshCw, CheckCircle2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ConfirmMeetingModal } from "../components/scheduling/ConfirmMeetingModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Scheduling() {
  const { token, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [pendingFollowups, setPendingFollowups] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showConfirmMeeting, setShowConfirmMeeting] = useState(false);
  const [activeTab, setActiveTab] = useState("links");
  
  // Create form state
  const [newLink, setNewLink] = useState({
    title: "",
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    project_id: "",
    meeting_type: "",
    assigned_to: "",
    duration_minutes: 60,
    buffer_minutes: 0,
    notes: "",
    expires_in_days: 7,
    available_slots: []
  });
  
  // Slot picker state
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotStartTime, setSlotStartTime] = useState("10:00");
  const [slotEndTime, setSlotEndTime] = useState("11:00");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [linksRes, appointmentsRes, followupsRes, teamRes, projectsRes] = await Promise.all([
        fetch(`${API_URL}/api/scheduling-links`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/appointments/pending-followup`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/team-members`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (linksRes.ok) setLinks(await linksRes.json());
      if (appointmentsRes.ok) setAppointments(await appointmentsRes.json());
      if (followupsRes.ok) setPendingFollowups(await followupsRes.json());
      if (teamRes.ok) setTeamMembers(await teamRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addSlot = () => {
    if (!selectedDate || !slotStartTime || !slotEndTime) {
      toast.error("Please select date and time");
      return;
    }
    
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const newSlot = {
      date: dateStr,
      start_time: slotStartTime,
      end_time: slotEndTime
    };
    
    // Check for duplicates
    const exists = newLink.available_slots.some(
      s => s.date === dateStr && s.start_time === slotStartTime
    );
    
    if (exists) {
      toast.error("This slot already exists");
      return;
    }
    
    setNewLink({
      ...newLink,
      available_slots: [...newLink.available_slots, newSlot]
    });
    
    toast.success("Time slot added");
  };

  const removeSlot = (index) => {
    setNewLink({
      ...newLink,
      available_slots: newLink.available_slots.filter((_, i) => i !== index)
    });
  };

  const addQuickSlots = (template) => {
    const today = new Date();
    const duration = newLink.duration_minutes || 60;
    let slots = [];
    
    const formatDateStr = (date) => {
      return format(date, "yyyy-MM-dd");
    };
    
    const generateSlotsForDay = (date, startHour, endHour) => {
      const result = [];
      for (let hour = startHour; hour < endHour; hour++) {
        const startTime = `${hour.toString().padStart(2, "0")}:00`;
        const endHourCalc = hour + Math.ceil(duration / 60);
        const endTime = `${endHourCalc.toString().padStart(2, "0")}:00`;
        
        if (endHourCalc <= endHour) {
          result.push({
            date: formatDateStr(date),
            start_time: startTime,
            end_time: endTime
          });
        }
      }
      return result;
    };
    
    switch (template) {
      case "morning":
        // Morning slots (9-12) for selected date or tomorrow
        const morningDate = selectedDate || new Date(today.getTime() + 24 * 60 * 60 * 1000);
        slots = generateSlotsForDay(morningDate, 9, 12);
        break;
        
      case "afternoon":
        // Afternoon slots (2-5pm = 14-17) for selected date or tomorrow
        const afternoonDate = selectedDate || new Date(today.getTime() + 24 * 60 * 60 * 1000);
        slots = generateSlotsForDay(afternoonDate, 14, 17);
        break;
        
      case "next7days":
        // One morning and one afternoon slot for next 7 days
        for (let i = 1; i <= 7; i++) {
          const date = new Date(today.getTime() + i * 24 * 60 * 60 * 1000);
          slots.push(
            { date: formatDateStr(date), start_time: "10:00", end_time: "11:00" },
            { date: formatDateStr(date), start_time: "15:00", end_time: "16:00" }
          );
        }
        break;
        
      case "weekdays":
        // Morning slot for next 5 weekdays
        let daysAdded = 0;
        let dayOffset = 1;
        while (daysAdded < 5) {
          const date = new Date(today.getTime() + dayOffset * 24 * 60 * 60 * 1000);
          const dayOfWeek = date.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Skip weekends
            slots.push(
              { date: formatDateStr(date), start_time: "10:00", end_time: "11:00" },
              { date: formatDateStr(date), start_time: "14:00", end_time: "15:00" }
            );
            daysAdded++;
          }
          dayOffset++;
        }
        break;
        
      default:
        break;
    }
    
    if (slots.length > 0) {
      // Filter out duplicates
      const existingKeys = new Set(newLink.available_slots.map(s => `${s.date}-${s.start_time}`));
      const newSlots = slots.filter(s => !existingKeys.has(`${s.date}-${s.start_time}`));
      
      setNewLink({
        ...newLink,
        available_slots: [...newLink.available_slots, ...newSlots]
      });
      toast.success(`Added ${newSlots.length} time slots`);
    }
  };

  const handleCreateLink = async () => {
    if (!newLink.title || !newLink.assigned_to || !newLink.meeting_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (newLink.available_slots.length === 0) {
      toast.error("Please add at least one available time slot");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/scheduling-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newLink)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(newLink.guest_name ? "Scheduling link created!" : "Anonymous scheduling link created!");
        
        // Copy link to clipboard
        navigator.clipboard.writeText(data.booking_url);
        toast.info("Link copied to clipboard!");
        
        setShowCreate(false);
        resetForm();
        fetchData();
      } else {
        toast.error("Failed to create link");
      }
    } catch (error) {
      toast.error("Error creating link");
    }
  };

  const resetForm = () => {
    setNewLink({
      title: "",
      guest_name: "",
      guest_email: "",
      guest_phone: "",
      project_id: "",
      meeting_type: "",
      assigned_to: "",
      duration_minutes: 60,
      buffer_minutes: 0,
      notes: "",
      expires_in_days: 7,
      available_slots: []
    });
    setSelectedDate(null);
  };

  const copyLink = (linkId) => {
    const url = `${window.location.origin}/book/${linkId}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  const cancelLink = async (linkId) => {
    try {
      const response = await fetch(`${API_URL}/api/scheduling-links/${linkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Link cancelled");
        fetchData();
      }
    } catch (error) {
      toast.error("Error cancelling link");
    }
  };

  const updateAppointmentOutcome = async (appointmentId, status) => {
    try {
      let endpoint;
      let method = "PUT";
      
      switch (status) {
        case "completed":
          endpoint = `/api/appointments/${appointmentId}/complete?send_summary_to_client=true`;
          break;
        case "no_show":
          endpoint = `/api/appointments/${appointmentId}/no-show?notify_guest=true`;
          break;
        case "rescheduled":
          // For reschedule, we need to prompt for new date/time - for now just mark
          endpoint = `/api/appointments/${appointmentId}/outcome`;
          method = "PUT";
          break;
        default:
          endpoint = `/api/appointments/${appointmentId}/outcome`;
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: status === "rescheduled" ? JSON.stringify({ status, send_update_to_client: true }) : undefined
      });
      
      if (response.ok) {
        toast.success(`Appointment marked as ${status}!`);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (error) {
      toast.error("Error updating appointment");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-[#4CAF50]/20 text-[#4CAF50]";
      case "booked": return "bg-[#2196F3]/20 text-[#2196F3]";
      case "completed": return "bg-[#4CAF50]/20 text-[#4CAF50]";
      case "cancelled": return "bg-[#FF4444]/20 text-[#FF4444]";
      case "expired": return "bg-[#888888]/20 text-[#888888]";
      case "scheduled": return "bg-[#FF6B35]/20 text-[#FF6B35]";
      default: return "bg-[#888888]/20 text-[#888888]";
    }
  };

  const timeOptions = [];
  for (let h = 8; h <= 20; h++) {
    for (let m of ["00", "30"]) {
      timeOptions.push(`${h.toString().padStart(2, "0")}:${m}`);
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="scheduling-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white">Appointment Scheduling</h1>
          <p className="text-[#888888] text-xs sm:text-sm mt-1">Create shareable links for clients and vendors to book meetings</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Create Scheduling Link
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1A1A1A] border-[#262626] w-[95vw] max-sm:w-full max-w-2xl max-h-[85dvh] overflow-y-auto mx-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create Scheduling Link</DialogTitle>
              <DialogDescription className="text-[#888]">
                Create a personalized booking link for your client or vendor
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Meeting Title *</label>
                  <Input
                    value={newLink.title}
                    onChange={(e) => setNewLink({...newLink, title: e.target.value})}
                    placeholder="e.g., Site Visit Discussion"
                    className="bg-[#111111] border-[#262626]"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Guest Name <span className="text-[#666] hidden sm:inline">(leave empty for anonymous)</span></label>
                  <Input
                    value={newLink.guest_name}
                    onChange={(e) => setNewLink({...newLink, guest_name: e.target.value})}
                    placeholder="Who is this meeting with?"
                    className="bg-[#111111] border-[#262626]"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Guest Email</label>
                  <Input
                    type="email"
                    value={newLink.guest_email}
                    onChange={(e) => setNewLink({...newLink, guest_email: e.target.value})}
                    placeholder="For sending invitation"
                    className="bg-[#111111] border-[#262626]"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Guest Phone</label>
                  <Input
                    value={newLink.guest_phone}
                    onChange={(e) => setNewLink({...newLink, guest_phone: e.target.value})}
                    placeholder="Optional"
                    className="bg-[#111111] border-[#262626]"
                  />
                </div>
              </div>

              {/* Meeting Type & Assigned To */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Meeting Type *</label>
                  <Input
                    value={newLink.meeting_type}
                    onChange={(e) => setNewLink({...newLink, meeting_type: e.target.value})}
                    placeholder="e.g., Site Visit, Online Call"
                    className="bg-[#111111] border-[#262626]"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Meeting With *</label>
                  <Select value={newLink.assigned_to} onValueChange={(v) => setNewLink({...newLink, assigned_to: v})}>
                    <SelectTrigger className="bg-[#111111] border-[#262626]">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name} - {member.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Duration & Project */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Duration (minutes)</label>
                  <Select value={newLink.duration_minutes.toString()} onValueChange={(v) => setNewLink({...newLink, duration_minutes: parseInt(v)})}>
                    <SelectTrigger className="bg-[#111111] border-[#262626]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-[#888888] mb-1 block">Link to Project</label>
                  <Select value={newLink.project_id || "none"} onValueChange={(v) => setNewLink({...newLink, project_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="bg-[#111111] border-[#262626]">
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {projects.map(project => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Available Slots */}
              <div className="border border-[#262626] rounded-lg p-3 sm:p-4">
                <label className="text-sm text-[#888888] mb-3 block">Available Time Slots *</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-left w-full">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "MMM dd") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-0 bg-[#1A1A1A] border-[#262626]" align="start" collisionPadding={12}>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="grid grid-cols-2 sm:contents gap-2">
                    <Select value={slotStartTime} onValueChange={setSlotStartTime}>
                      <SelectTrigger className="bg-[#111111] border-[#262626]">
                        <SelectValue placeholder="Start" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={slotEndTime} onValueChange={setSlotEndTime}>
                      <SelectTrigger className="bg-[#111111] border-[#262626]">
                        <SelectValue placeholder="End" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="button" variant="outline" onClick={addSlot} className="mb-4 w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-1" /> Add Slot
                </Button>
                
                {/* Quick Slot Templates */}
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-4">
                  <Button type="button" size="sm" variant="ghost" className="text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10" onClick={() => addQuickSlots("morning")}>
                    + Morning (9-12)
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10" onClick={() => addQuickSlots("afternoon")}>
                    + Afternoon (2-5)
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10" onClick={() => addQuickSlots("next7days")}>
                    + Next 7 Days
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10" onClick={() => addQuickSlots("weekdays")}>
                    + Weekdays Only
                  </Button>
                </div>
                
                {newLink.available_slots.length > 0 && (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {newLink.available_slots.map((slot, i) => (
                      <div key={`${slot.date}-${slot.start_time}-${i}`} className="flex items-center justify-between bg-[#111111] p-2 rounded">
                        <span className="text-sm text-white">
                          {slot.date} | {slot.start_time} - {slot.end_time}
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => removeSlot(i)}>
                          <X className="h-4 w-4 text-[#FF4444]" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Notes for Guest</label>
                <Textarea
                  value={newLink.notes}
                  onChange={(e) => setNewLink({...newLink, notes: e.target.value})}
                  placeholder="Any instructions or context..."
                  className="bg-[#111111] border-[#262626]"
                />
              </div>

              <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 px-6 py-4 max-sm:pb-[calc(1rem+env(safe-area-inset-bottom))] bg-[#1A1A1A] border-t border-[#262626] flex justify-end gap-2" data-testid="create-link-footer">
                <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => { setShowCreate(false); resetForm(); }} data-testid="create-link-cancel-btn">Cancel</Button>
                <Button onClick={handleCreateLink} className="flex-1 sm:flex-none bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="create-link-submit-btn">
                  Create & Copy Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Button
          variant="outline"
          onClick={() => setShowConfirmMeeting(true)}
          className="border-[#FF6B35]/40 text-[#FF6B35] hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] w-full sm:w-auto"
          data-testid="open-confirm-meeting-btn"
        >
          <MailCheck className="h-4 w-4 mr-2" />
          Send Meeting Confirmation
        </Button>
        <ConfirmMeetingModal open={showConfirmMeeting} onOpenChange={setShowConfirmMeeting} />
      </div>

      {/* Pending Follow-ups Alert */}
      {pendingFollowups.length > 0 && (
        <Card className="bg-[#2a1a10] border-[#FF6B35]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-[#FF6B35]" />
              <div>
                <p className="text-[#FF6B35] font-medium">{pendingFollowups.length} appointments need follow-up</p>
                <p className="text-[#888888] text-sm">Mark them as completed, rescheduled, or no-show</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 border-b border-[#262626] overflow-x-auto">
        {["links", "appointments", "followups"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab
                ? "text-[#FF6B35] border-b-2 border-[#FF6B35]"
                : "text-[#888888] hover:text-white"
            }`}
          >
            {tab === "links" && "Links"}
            {tab === "appointments" && `Booked (${appointments.filter(a => a.status === "scheduled").length})`}
            {tab === "followups" && `Follow-up (${pendingFollowups.length})`}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid gap-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-[#262626] rounded animate-pulse"></div>)}
        </div>
      ) : (
        <>
          {/* Links Tab */}
          {activeTab === "links" && (
            <div className="grid gap-4">
              {links.length === 0 ? (
                <Card className="bg-[#1A1A1A] border-[#262626]">
                  <CardContent className="p-12 text-center">
                    <Link2 className="h-12 w-12 text-[#888888] mx-auto mb-4" />
                    <p className="text-[#888888]">No scheduling links yet. Create one to share with clients or vendors.</p>
                  </CardContent>
                </Card>
              ) : (
                links.map(link => (
                  <Card key={link.link_id} className="bg-[#1A1A1A] border-[#262626]">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                            <h3 className="text-white font-medium text-sm sm:text-base truncate">{link.title}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${getStatusColor(link.status)}`}>
                              {link.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-[#888888]">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" /> {link.guest_name || <span className="text-[#FF6B35]">Anonymous</span>}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {link.duration_minutes}m
                            </span>
                            <span className="hidden sm:inline">{link.meeting_type}</span>
                            <span>{link.available_slots?.length || 0} slots</span>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {link.status === "active" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => copyLink(link.link_id)} className="text-xs">
                                <Copy className="h-3 w-3 sm:mr-1" /> <span className="hidden sm:inline">Copy</span>
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => cancelLink(link.link_id)}>
                                <Trash2 className="h-3 w-3 text-[#FF4444]" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === "appointments" && (
            <div className="grid gap-4">
              {appointments.filter(a => a.status === "scheduled").length === 0 ? (
                <Card className="bg-[#1A1A1A] border-[#262626]">
                  <CardContent className="p-12 text-center">
                    <CalendarIcon className="h-12 w-12 text-[#888888] mx-auto mb-4" />
                    <p className="text-[#888888]">No upcoming appointments.</p>
                  </CardContent>
                </Card>
              ) : (
                appointments.filter(a => a.status === "scheduled").map(apt => (
                  <Card key={apt.appointment_id} className="bg-[#1A1A1A] border-[#262626]">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium text-sm sm:text-base">{apt.meeting_type} with {apt.guest_name}</h3>
                          <p className="text-[#888888] text-xs sm:text-sm">{apt.scheduled_date} at {apt.scheduled_time}</p>
                          <p className="text-[#666666] text-xs truncate">{apt.guest_email} {apt.guest_phone && `| ${apt.guest_phone}`}</p>
                          {apt.guest_company && <p className="text-[#555] text-xs">{apt.guest_company}</p>}
                        </div>
                        <div className="flex sm:flex-col gap-2 items-center sm:items-end justify-between sm:justify-start">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(apt.status)}`}>
                            {apt.status}
                          </span>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => window.open(`${API_URL}/api/appointments/${apt.appointment_id}/calendar.ics`, '_blank')}
                              title="Download Calendar"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => updateAppointmentOutcome(apt.appointment_id, "completed")} title="Mark Completed">
                              <CheckCircle2 className="h-3 w-3 text-[#4CAF50]" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => updateAppointmentOutcome(apt.appointment_id, "no_show")} title="Mark No-Show">
                              <X className="h-3 w-3 text-[#FF4444]" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Follow-ups Tab */}
          {activeTab === "followups" && (
            <div className="grid gap-4">
              {pendingFollowups.length === 0 ? (
                <Card className="bg-[#1A1A1A] border-[#262626]">
                  <CardContent className="p-12 text-center">
                    <Check className="h-12 w-12 text-[#4CAF50] mx-auto mb-4" />
                    <p className="text-[#888888]">All appointments are up to date!</p>
                  </CardContent>
                </Card>
              ) : (
                pendingFollowups.map(apt => (
                  <Card key={apt.appointment_id} className="bg-[#1A1A1A] border-[#262626]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-medium">{apt.meeting_type} with {apt.guest_name}</h3>
                          <p className="text-[#888888] text-sm">Was scheduled: {apt.scheduled_date} at {apt.scheduled_time}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => updateAppointmentOutcome(apt.appointment_id, "completed")} className="bg-[#4CAF50] hover:bg-[#4CAF50]/90">
                            <Check className="h-3 w-3 mr-1" /> Completed
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateAppointmentOutcome(apt.appointment_id, "rescheduled")}>
                            Rescheduled
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateAppointmentOutcome(apt.appointment_id, "no_show")} className="text-[#FF4444] border-[#FF4444]">
                            No-show
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
